<template>
  <div>
    <p class="text-muted">
      The catalogue of document titles. Which clients are asked for which document is set by the
      upload items of each package.
    </p>

    <b-overlay :show="loading">
      <b-row align-h="between" class="mb-1">
        <b-col cols="12" md="3">
          <b-form-input v-model="filter" placeholder="Search..." />
        </b-col>
        <b-col cols="12" md="3" class="d-flex justify-content-end">
          <b-button variant="success" @click="openCreateModal">
            New document type
          </b-button>
        </b-col>
      </b-row>

      <b-table
        striped
        hover
        responsive
        class="builder-table"
        :items="documents"
        :filter="filter"
        :fields="tableColumns"
        :current-page="currentPage"
        :per-page="perPage"
        show-empty
        empty-text="No onboarding document types defined yet."
        @filtered="onFiltered"
      >
        <template #cell(index)="data">
          <strong class="text-primary">#{{ data.index + 1 }}</strong>
        </template>

        <template #cell(PackageItemCount)="{ value }">
          <span v-if="value">Used in {{ value }} package item{{ value === 1 ? '' : 's' }}</span>
          <span v-else class="text-muted">Not used in any package</span>
        </template>

        <template #cell(actions)="data">
          <b-button size="sm" variant="flat-primary" class="btn-icon" title="Edit" @click="openEditModal(data.item)">
            <feather-icon icon="EditIcon" />
          </b-button>
          <b-button size="sm" variant="flat-danger" class="btn-icon" title="Delete" @click="confirmDelete(data.item)">
            <feather-icon icon="TrashIcon" />
          </b-button>
        </template>
      </b-table>

      <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" />
    </b-overlay>

    <!-- Create / Edit Modal -->
    <b-modal
      v-model="showModal"
      :title="form.id ? 'Edit Document Type' : 'New Document Type'"
      ok-title="Save"
      :ok-disabled="modalLoading"
      no-close-on-backdrop
      @ok="handleSave"
    >
      <b-overlay :show="modalLoading">
        <b-form @submit.prevent>
          <b-form-group label="Document title">
            <b-form-input v-model="form.Name" placeholder="e.g. Certified copy of passport" autofocus />
            <small class="text-muted">
              Renaming a document type also renames it in the packages and onboardings that use it.
            </small>
          </b-form-group>
        </b-form>
      </b-overlay>
    </b-modal>

    <!-- Delete Modal -->
    <b-modal
      v-model="showDeleteModal"
      title="Delete Document Type"
      ok-title="Delete"
      ok-variant="danger"
      :ok-disabled="deleteLoading"
      no-close-on-backdrop
      @ok="handleDelete"
    >
      <b-overlay :show="deleteLoading">
        <p>
          Delete <strong>{{ deleteTarget ? deleteTarget.Name : "" }}</strong>?
          <span v-if="deleteTarget && deleteTarget.PackageItemCount">
            It is used in {{ deleteTarget.PackageItemCount }} package item(s); those items are removed too.
          </span>
        </p>
        <b-form-group label="Apply this change to">
          <b-form-radio-group v-model="deleteApplyToExisting" stacked>
            <b-form-radio :value="false">
              New onboardings only
              <small class="text-muted d-block">
                Clients already asked for this document are still asked for it.
              </small>
            </b-form-radio>
            <b-form-radio :value="true">
              Also remove it from existing onboardings that list it
              <small class="text-muted d-block">
                Withdraws the request from the {{ existingOnboardingCount }} onboarding(s)
                already under way. Documents clients have already uploaded are kept.
              </small>
            </b-form-radio>
          </b-form-radio-group>
        </b-form-group>
      </b-overlay>
    </b-modal>
  </div>
</template>

<script>
import {
  BRow, BCol, BButton, BTable, BPagination,
  BFormInput, BFormGroup, BForm,
  BFormRadio, BFormRadioGroup, BModal, BOverlay,
} from 'bootstrap-vue'
import store from '@/store'
import { apiError } from './builderHelpers'

function emptyForm() {
  return { id: null, Name: '' }
}

export default {
  name: 'DocumentsTab',

  components: {
    BRow, BCol, BButton, BTable, BPagination,
    BFormInput, BFormGroup, BForm,
    BFormRadio, BFormRadioGroup, BModal, BOverlay,
  },

  data() {
    return {
      loading: false,
      modalLoading: false,
      deleteLoading: false,
      documents: [],
      existingOnboardingCount: 0,
      filter: '',
      currentPage: 1,
      perPage: 15,
      totalRows: 0,
      showModal: false,
      showDeleteModal: false,
      deleteTarget: null,
      deleteApplyToExisting: false,
      form: emptyForm(),
      tableColumns: [
        { key: 'index', label: '' },
        { key: 'Name', label: 'Document title', sortable: true },
        { key: 'PackageItemCount', label: 'Usage', sortable: true },
        { key: 'actions', label: 'Actions' },
      ],
    }
  },

  mounted() {
    this.loadDocuments()
  },

  methods: {
    loadDocuments() {
      this.loading = true
      store.dispatch('settings/getOnboardingDocumentSettings')
        .then(res => {
          this.documents = res.data.documents || []
          this.existingOnboardingCount = res.data.existingOnboardingCount || 0
          this.totalRows = this.documents.length
        })
        .catch(() => {
          this.$bvToast.toast('Failed to load onboarding documents.', { variant: 'danger', title: 'Error', solid: true })
        })
        .finally(() => { this.loading = false })
    },

    // Reload this tab and tell the builder so the document catalogue used by packages is fresh.
    afterChange() {
      this.loadDocuments()
      this.$emit('refresh')
    },

    openCreateModal() {
      this.form = emptyForm()
      this.showModal = true
    },

    openEditModal(item) {
      this.form = { id: item.id, Name: item.Name }
      this.showModal = true
    },

    handleSave(bvModalEvt) {
      bvModalEvt.preventDefault()

      if (!this.form.Name.trim()) {
        this.$bvToast.toast('Document title is required.', { variant: 'danger', title: 'Validation', solid: true })
        return
      }

      this.modalLoading = true
      store.dispatch('settings/saveOnboardingDocumentSetting', {
        id: this.form.id,
        name: this.form.Name.trim(),
      })
        .then(res => {
          this.$bvToast.toast(res.data.message || 'Document type saved.', { variant: 'success', title: 'Success', solid: true })
          this.showModal = false
          this.afterChange()
        })
        .catch(err => {
          this.$bvToast.toast(apiError(err), { variant: 'danger', title: 'Error', solid: true })
        })
        .finally(() => { this.modalLoading = false })
    },

    confirmDelete(item) {
      this.deleteTarget = item
      this.deleteApplyToExisting = false
      this.showDeleteModal = true
    },

    handleDelete(bvModalEvt) {
      bvModalEvt.preventDefault()

      this.deleteLoading = true
      store.dispatch('settings/deleteOnboardingDocumentSetting', {
        id: this.deleteTarget.id,
        applyToExisting: this.deleteApplyToExisting,
      })
        .then(res => {
          this.$bvToast.toast(res.data.message || 'Document type deleted.', { variant: 'success', title: 'Deleted', solid: true })
          this.showDeleteModal = false
          this.afterChange()
        })
        .catch(err => {
          this.$bvToast.toast(apiError(err), { variant: 'danger', title: 'Error', solid: true })
        })
        .finally(() => { this.deleteLoading = false })
    },

    onFiltered(items) {
      this.totalRows = items.length
      this.currentPage = 1
    },
  },
}
</script>
